import { Component, Input, OnInit } from '@angular/core';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    CollectionCheck,
    ImageViewableStatus
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Modal, StatusColor } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { ExpandableRowsService } from '@lsq-frontend/common-ui';
import { PopoverPlacement } from 'ngx-smart-popover';
import { of } from 'rxjs';
import {
    CHARGEBACKS_DESCRIPTION,
    NON_FACTORED_DESCRIPTION,
    COLLECTION_FEE_DESCRIPTION,
    COLLECTION_PREVIOUS_PAYMENTS_DESCRIPTION,
    COLLECTION_INVOICE_BALANCE_DESCRIPTION
} from '../../../../constants/copy.constants';
import { EnumDisplay } from '../../../../constants/enum-display.constants';
import { ImagePreviewModalComponent } from '../../../image-preview/image-preview-modal.component';
@Component({
    selector: 'collections-mobile-grouped-table',
    template: `
        <lsq-data-table-mobile [dataSource]="dataSource" [initializeFirstOpen]="groupIndex === 0">
            <ng-template #mobileCardHeader let-row="row">
                <div class="purchases-mobile-table-header">
                    <div class="mtc-header-content">
                        <div class="mtc-header-super-title">
                            {{ row.debtorName }}
                        </div>
                        <ng-container *ngIf="!!row.checkNumber">
                            <a
                                *ngIf="isCheckNumberClickable(row)"
                                class="check-number"
                                href="javascript:void(0)"
                                (click)="handleCheckNumberClick($event, row)"
                            >
                                Check #{{ row.checkNumber }}
                            </a>

                            <!-- Chargeback -->
                            <span *ngIf="!isCheckNumberClickable(row)" class="chargeback-label">
                                Check #{{ row.checkNumber }}
                            </span>
                        </ng-container>
                        <div class="mtc-header-sub-title">
                            Payment Amount: {{ row.checkPaymentAmount | lsqCurrency }}
                        </div>
                    </div>
                </div>
                <ng-template #mobileCardColumns let-row="row">
                    <div class="card-item">
                        <div class="card-item-label">
                            Total Invoice Amount
                        </div>
                        <div class="card-item-value">
                            {{ row.checkInvoiceAmount | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            Total Chargebacks
                            <lsq-icon-popover
                                [text]="checkChargebackPopoverContent"
                            ></lsq-icon-popover>
                        </div>
                        <div class="card-item-value">
                            {{ row.checkChargebacks | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            Total Non-Factored
                            <lsq-icon-popover [text]="nonFactoredPopoverContent"></lsq-icon-popover>
                        </div>
                        <div class="card-item-value">
                            {{ row.checkNonFactored | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            Total Previous Payments
                            <lsq-icon-popover
                                [text]="previousPaymentsPopoverContent"
                            ></lsq-icon-popover>
                        </div>
                        <div class="card-item-value">
                            {{ row.checkPreviousPayments | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            Total Invoice Balance
                            <lsq-icon-popover
                                [text]="invoiceBalancePopoverContent"
                            ></lsq-icon-popover>
                        </div>
                        <div class="card-item-value">
                            {{ row.checkInvoiceBalance | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            Total Collection Fees
                            <lsq-icon-popover
                                [text]="collectionFeePopoverContent"
                            ></lsq-icon-popover>
                        </div>
                        <div class="card-item-value">
                            {{ row.checkCollectionFees | lsqCurrency }}
                        </div>
                    </div>
                </ng-template>
                <ng-template #mobileCardFooter let-row="row">
                    <div class="mtc-footer-link">
                        <lsq-button
                            appearance="transparent"
                            (buttonClick)="handleMobileTableCardFooterClick(row)"
                        >
                            Invoice Details
                        </lsq-button>
                    </div>
                </ng-template>
            </ng-template>
        </lsq-data-table-mobile>
    `,
    styleUrls: ['./collections.mobile-grouped-table.components.scss']
})
export class CollectionsMobileGroupedTableComponent implements OnInit {
    @Input() public rows: CollectionCheck[];
    @Input() public groupIndex: number;
    @Input() public expandableRowsService: ExpandableRowsService;

    public dataSource: LsqListSource<CollectionCheck>;

    constructor(public modalService: ModalService) {}

    // Constants.
    public statusColor = StatusColor;
    public icons = Icons;
    public enumDisplay = EnumDisplay;
    public placement = PopoverPlacement;
    public checkChargebackPopoverContent = CHARGEBACKS_DESCRIPTION;
    public nonFactoredPopoverContent = NON_FACTORED_DESCRIPTION;
    public collectionFeePopoverContent = COLLECTION_FEE_DESCRIPTION;
    public previousPaymentsPopoverContent = COLLECTION_PREVIOUS_PAYMENTS_DESCRIPTION;
    public invoiceBalancePopoverContent = COLLECTION_INVOICE_BALANCE_DESCRIPTION;

    public ngOnInit(): void {
        this.dataSource = new LsqListSource<CollectionCheck>({
            rows$: of(this.rows)
        });
    }

    public isCheckNumberClickable(check: CollectionCheck): boolean {
        return (
            check.imageViewableStatus !== ImageViewableStatus.NotViewable &&
            !!check.checkKey &&
            check.checkAmount > 0
        );
    }

    public async handleCheckNumberClick(event: MouseEvent, check: CollectionCheck): Promise<void> {
        event.stopImmediatePropagation();

        if (check.imageViewableStatus === ImageViewableStatus.Viewable) {
            this.modalService.showModal(
                new Modal({
                    id: 'image-preview-modal',
                    title: `Check #${check.checkNumber}`,
                    component: ImagePreviewModalComponent,
                    width: 'auto',
                    data: {
                        title: `Check #${check.checkNumber}`,
                        checkKey: check.checkKey,
                        filename: `check_${check.checkNumber}`
                    }
                })
            );
        } else if (check.imageViewableStatus === ImageViewableStatus.Archived) {
            this.modalService.showModal(
                new Modal({
                    id: 'check-archived-modal',
                    title: `Check #${check.checkNumber}`,
                    bodyHtml:
                        '<p>This check image is not available to view. You can request a copy of this check from the <a href="mailto:accountmanagers@lsq.com">Client Services team.</a></p>',
                    width: 'auto',
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Close'
                })
            );
        }
    }

    public handleMobileTableCardFooterClick(row: any): void {
        this.expandableRowsService.expand(row);
    }
}
